import React, { useEffect, useState } from 'react';
import parse from 'html-react-parser';
import { useParams } from 'react-router-dom';
import {
    Box,
    Container,
    Typography,
    Card,
    CardContent,
    CardMedia,
    Grid,
    CircularProgress,
    Alert,
    Paper,
    Chip,
    Divider,
    IconButton,
    Breadcrumbs,
    Link,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow
} from '@mui/material';
import { Avatar, List, ListItem, ListItemAvatar, ListItemText } from '@mui/material';
import {
    ArrowBack,
    Share,
    Favorite,
    FavoriteBorder,
    TrendingUp,
    Store
} from '@mui/icons-material';
import { Line } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend
} from 'chart.js';
import { client } from '../api/Api';
import { Helmet } from '../lib/HelmetShim';
import ProductCard from '../components/ProductCard';

// Register Chart.js components
ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend
);

const DEFAULT_IMAGE_PATH = "/img/img.png";

const getImageUrl = (imagePath) => {
    if (!imagePath) return process.env.PUBLIC_URL + DEFAULT_IMAGE_PATH;
    return `https://tikrai.com/api/images/${imagePath}`;
};

const useMobileDetection = () => {
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        const checkMobile = () => {
            const width = window.innerWidth;
            const isMobileDevice = width < 900 || /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
            setIsMobile(isMobileDevice);
        };

        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    return isMobile;
};

function Product() {
    const { productName } = useParams();
    const [product, setProduct] = useState(null);
    const [priceHistory, setPriceHistory] = useState([]);
    const [similarProducts, setSimilarProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isFavorite, setIsFavorite] = useState(false);
    const [recipe, setRecipe] = useState(null);
    const [recipeLoading, setRecipeLoading] = useState(false);
    const isMobile = useMobileDetection();

    useEffect(() => {
        const fetchProductData = async () => {
            try {
                setLoading(true);

                // Fetch product details
                const productResponse = await client.get(`/products/alias/${encodeURIComponent(productName)}`);
                setProduct(productResponse.data);

                // Fetch price history for the last year
                const priceResponse = await client.get(`/products/prices/${encodeURIComponent(productName)}`);
                setPriceHistory(priceResponse.data);

                // Fetch similar products
                const similarResponse = await client.get(`/products/similar/${encodeURIComponent(productName)}`);
                setSimilarProducts(similarResponse.data);
            } catch (err) {
                setError('Failed to load product data');
                console.error('Error fetching product data:', err);
            } finally {
                setLoading(false);
            }
        };

        if (productName) {
            fetchProductData();
        }
    }, [productName]);

    // Fetch or generate recipe separately so the rest of the page does not wait
    useEffect(() => {
        let isMounted = true;
        const fetchRecipe = async () => {
            try {
                setRecipeLoading(true);
                const recipeResp = await client.get(`/products/alias/${encodeURIComponent(productName)}/recipe`, { validateStatus: () => true });
                if (!isMounted) return;
                if (recipeResp.status === 200) {
                    setRecipe(recipeResp.data);
                } else {
                    setRecipe(null);
                }
            } catch (e) {
                if (isMounted) console.warn('Recipe fetch failed', e);
            } finally {
                if (isMounted) setRecipeLoading(false);
            }
        };
        if (productName) fetchRecipe();
        return () => { isMounted = false; };
    }, [productName]);

    // Poll for recipe illustration until available
    useEffect(() => {
        if (!productName) return;
        if (!recipe || recipe.imageUrl) return; // nothing to poll
        let cancelled = false;
        const interval = setInterval(async () => {
            try {
                const resp = await client.get(`/products/alias/${encodeURIComponent(productName)}/recipe`, { validateStatus: () => true });
                if (cancelled) return;
                if (resp.status === 200) {
                    // update only if imageUrl appeared to avoid flicker
                    if (resp.data && resp.data.imageUrl) {
                        setRecipe(resp.data);
                        clearInterval(interval);
                    }
                }
            } catch (e) {
                // ignore polling errors
            }
        }, 3000);
        // stop polling after 2 minutes
        const timeout = setTimeout(() => clearInterval(interval), 120000);
        return () => { cancelled = true; clearInterval(interval); clearTimeout(timeout); };
    }, [productName, recipe && !recipe.imageUrl]);

    const handleFavoriteToggle = () => {
        setIsFavorite(!isFavorite);
        // Here you would typically make an API call to update favorites
    };

    const handleShare = () => {
        if (navigator.share) {
            navigator.share({
                title: product?.name,
                text: `Check out this product: ${product?.name}`,
                url: window.location.href,
            });
        } else {
            // Fallback for browsers that don't support Web Share API
            navigator.clipboard.writeText(window.location.href);
        }
    };

    const getPriceChartData = () => {
        if (!priceHistory.length) return null;

        // Group prices by provider
        const providerData = {};
        priceHistory.forEach(price => {
            const providerName = price.store?.name || 'Unknown';
            if (!providerData[providerName]) {
                providerData[providerName] = {
                    label: providerName,
                    data: [],
                    labels: [],
                    borderColor: getProviderColor(providerName),
                    backgroundColor: getProviderColor(providerName) + '20',
                    tension: 0.1,
                };
            }
            providerData[providerName].data.push(price.value);
            providerData[providerName].labels.push(new Date(price.date).toLocaleDateString());
        });

        // Get all unique dates for x-axis
        const allDates = [...new Set(priceHistory.map(p => new Date(p.date).toLocaleDateString()))].sort();

        return {
            labels: allDates,
            datasets: Object.values(providerData).map(provider => ({
                ...provider,
                data: allDates.map(date => {
                    const priceForDate = priceHistory.find(p =>
                        new Date(p.date).toLocaleDateString() === date &&
                        (p.store?.name || 'Unknown') === provider.label
                    );
                    return priceForDate ? priceForDate.value : null;
                }).filter(val => val !== null)
            }))
        };
    };

    const getProviderColor = (providerName) => {
        const colors = {
            'Provider 1': '#1976d2',
            'Provider 2': '#388e3c',
            'Provider 3': '#f57c00',
            'Provider 4': '#d32f2f',
            'Provider 5': '#7b1fa2',
        };
        return colors[providerName] || '#424242';
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'top',
            },
            title: {
                display: true,
                text: 'Price History (Last Year)',
            },
        },
        scales: {
            y: {
                beginAtZero: false,
                title: {
                    display: true,
                    text: 'Price (€)',
                },
            },
            x: {
                title: {
                    display: true,
                    text: 'Date',
                },
            },
        },
    };

    if (loading) {
        return (
            <Container maxWidth="xl" sx={{ py: 4 }}>
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                    <CircularProgress size={60} />
                </Box>
            </Container>
        );
    }

    if (error) {
        return (
            <Container maxWidth="xl" sx={{ py: 4 }}>
                <Alert severity="error" sx={{ maxWidth: 600, mx: 'auto' }}>
                    {error}
                </Alert>
            </Container>
        );
    }

    if (!product) {
        return (
            <Container maxWidth="xl" sx={{ py: 4 }}>
                <Alert severity="info" sx={{ maxWidth: 600, mx: 'auto' }}>
                    Product not found
                </Alert>
            </Container>
        );
    }

    const chartData = getPriceChartData();

    // SEO metadata
    const siteUrl = 'https://tikrai.com';
    const canonicalUrl = `${siteUrl}/product/${encodeURIComponent(product.alias || productName)}`;
    const title = product?.name ? `${product.name} | TIKRAI` : 'Produktas | TIKRAI';
    const description = (() => {
        const raw = product?.description || '';
        const tmp = document.createElement('div');
        tmp.innerHTML = raw;
        const text = tmp.textContent || tmp.innerText || '';
        return text.trim().substring(0, 300);
    })();
    const imageUrl = product?.image ? `${getImageUrl(product.image)}.png` : `${process.env.PUBLIC_URL}${DEFAULT_IMAGE_PATH}`;
    const productJsonLd = {
        '@context': 'https://schema.org',
        '@type': 'Product',
        name: product?.name || '',
        image: [imageUrl],
        description: description,
        sku: product?.id ? String(product.id) : undefined,
        brand: product?.category?.name ? { '@type': 'Brand', name: product.category.name } : undefined,
        offers: priceHistory && priceHistory.length ? {
            '@type': 'AggregateOffer',
            lowPrice: String(Math.min(...priceHistory.map(p => p.value))),
            highPrice: String(Math.max(...priceHistory.map(p => p.value))),
            priceCurrency: 'EUR',
            offerCount: String(priceHistory.length),
            url: canonicalUrl,
            availability: 'https://schema.org/InStock'
        } : undefined
    };

    return (
        <Container maxWidth="xl" className="container" sx={{ py: { xs: 2, sm: 4 } }}>
            <Helmet>
                <title>{title}</title>
                <meta name="description" content={description} />
                <link rel="canonical" href={canonicalUrl} />
                <meta property="og:type" content="product" />
                <meta property="og:title" content={title} />
                <meta property="og:description" content={description} />
                <meta property="og:url" content={canonicalUrl} />
                <meta property="og:image" content={imageUrl} />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={title} />
                <meta name="twitter:description" content={description} />
                <meta name="twitter:image" content={imageUrl} />
                <script type="application/ld+json">{JSON.stringify(productJsonLd)}</script>
            </Helmet>
            {/* Breadcrumbs */}
            <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
                <Link color="inherit" href="/" sx={{ textDecoration: 'none' }}>
                    Home
                </Link>
                {product.category && (
                    <Link color="inherit" href={`/category/${product.category.id}`} sx={{ textDecoration: 'none' }}>
                        {product.category.name}
                    </Link>
                )}
                <Typography color="text.primary">{product.name}</Typography>
            </Breadcrumbs>

            {/* Product Details */}
            <Box className="card shadow-soft" sx={{ p: { xs: 2, sm: 3 }, mb: 4, borderRadius: 2, border: '1px solid var(--line)', background: '#fff' }}>
                <Grid container spacing={4}>
                    {/* Product Image (max 1/3 width) */}
                    <Grid item xs={12} md={4}>
                        <Box sx={{ position: 'relative', borderRadius: 2, overflow: 'hidden' }}>
                            <CardMedia
                                component="img"
                                image={
                                    product.image
                                        ? `${getImageUrl(product.image)}.png`
                                        : `${process.env.PUBLIC_URL}${DEFAULT_IMAGE_PATH}`
                                }
                                alt={product.name}
                                onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = `${process.env.PUBLIC_URL}${DEFAULT_IMAGE_PATH}`; }}
                                sx={{
                                    objectFit: 'contain',
                                    width: '100%',
                                    height: 'auto',
                                    maxHeight: { xs: 300, md: 400 },
                                    display: 'block',
                                    backgroundColor: '#f7f7f7'
                                }}
                            />
                            {/* Price badges overlay: show previous and current */}
                            {(() => {
                                const curr = product?.lastPrice != null && product.lastPrice !== ''
                                    ? Number(product.lastPrice)
                                    : (priceHistory && priceHistory.length > 0 ? Number(priceHistory[priceHistory.length-1].value) : null);
                                const prev = product?.previousPrice != null && product.previousPrice !== ''
                                    ? Number(product.previousPrice)
                                    : null;
                                return curr != null ? (
                                    <>
                                        {prev != null && prev !== curr && (
                                            <Chip
                                                label={`€${prev.toFixed(2)}`}
                                                sx={{
                                                    position: 'absolute',
                                                    top: 8,
                                                    left: 8,
                                                    backgroundColor: 'rgba(255,255,255,0.9)',
                                                    color: 'text.primary',
                                                    fontWeight: 700,
                                                    border: '1px solid var(--line)',
                                                    '& .MuiChip-label': { padding: '6px 10px', textDecoration: 'line-through' }
                                                }}
                                                variant="outlined"
                                            />
                                        )}
                                        <Chip
                                            label={`€${curr.toFixed(2)}`}
                                            sx={{
                                                position: 'absolute',
                                                top: 8,
                                                right: 8,
                                                backgroundColor: 'var(--primary)',
                                                color: 'var(--white)',
                                                fontWeight: 700,
                                                '& .MuiChip-label': { padding: '6px 10px' }
                                            }}
                                        />
                                    </>
                                ) : null;
                            })()}
                        </Box>
                    </Grid>

                    {/* Product Information (2/3 width) */}
                    <Grid item xs={12} md={8}>
                        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                <Typography variant={isMobile ? "h5" : "h4"} component="h1" sx={{ fontWeight: 800 }}>
                                    {product.name}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <IconButton onClick={handleFavoriteToggle} sx={{ color: 'var(--primary)' }}>
                                        {isFavorite ? <Favorite /> : <FavoriteBorder />}
                                    </IconButton>
                                    <IconButton onClick={handleShare} sx={{ color: 'var(--primary)' }}>
                                        <Share />
                                    </IconButton>
                                </Box>
                            </Box>

                            {/* Nested grid: center description, right current prices */}
                            <Grid container spacing={2}>
                                {/* Center: description and nutrition */}
                                <Grid item xs={12} md={8}>
                                    {product.category && (
                                        <Chip
                                            label={product.category.name}
                                            sx={{ mb: 2, alignSelf: 'flex-start', fontWeight: 700, background: 'var(--bg-10)', color: 'var(--primary)' }}
                                        />
                                    )}

                                    {(() => {
                                        // Parse descriptionJSON to extract brand and nutrition
                                        let data = null;
                                        try {
                                            data = product?.descriptionJSON ? JSON.parse(product.descriptionJSON) : null;
                                        } catch (e) {
                                            data = null;
                                        }
                                        const brand = data?.brand || null;
                                        return brand ? (
                                            <Chip
                                                label={brand}
                                                color="default"
                                                sx={{ mb: 2, alignSelf: 'flex-start', fontWeight: 600, background: 'var(--bg-10)' }}
                                            />
                                        ) : null;
                                    })()}

                                    <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8, color: 'text.secondary' }}>
                                        {parse(product.description || 'No description available for this product.')}
                                    </Typography>

                                    {(() => {
                                        let data = null;
                                        try {
                                            data = product?.descriptionJSON ? JSON.parse(product.descriptionJSON) : null;
                                        } catch (e) {
                                            data = null;
                                        }
                                        const nutrition = data?.nutrition;
                                        if (!nutrition || typeof nutrition !== 'object') return null;

                                        const rows = [
                                            { key: 'per', label: 'Porcija' },
                                            { key: 'energy_kcal', label: 'Energija (kcal)' },
                                            { key: 'energy_kJ', label: 'Energija (kJ)' },
                                            { key: 'fat_g', label: 'Riebalai (g)' },
                                            { key: 'saturated_fat_g', label: 'Soč. riebalai (g)' },
                                            { key: 'carbs_g', label: 'Angliavandeniai (g)' },
                                            { key: 'sugar_g', label: 'Cukrūs (g)' },
                                            { key: 'fiber_g', label: 'Skaidulos (g)' },
                                            { key: 'protein_g', label: 'Baltymai (g)' },
                                            { key: 'salt_g', label: 'Druska (g)' },
                                        ];

                                        // Some sources may use alt keys
                                        const altMap = {
                                            sugars_g: 'sugar_g',
                                            carbohydrate_g: 'carbs_g',
                                            carbohydrates_g: 'carbs_g',
                                            proteins_g: 'protein_g',
                                            protein: 'protein_g',
                                            fat: 'fat_g',
                                            saturatedFat_g: 'saturated_fat_g',
                                            energyKcal: 'energy_kcal',
                                            energyKJ: 'energy_kJ',
                                        };

                                        const normalized = { ...nutrition };
                                        Object.entries(altMap).forEach(([from, to]) => {
                                            if (normalized[from] != null && normalized[to] == null) {
                                                normalized[to] = normalized[from];
                                            }
                                        });

                                        const hasAny = rows.some(r => normalized[r.key] != null && normalized[r.key] !== '');
                                        if (!hasAny) return null;

                                        return (
                                            <TableContainer component={Paper} elevation={0} sx={{ mb: 3, border: '1px solid var(--line)', borderRadius: 2 }}>
                                                <Table size="small" sx={{ '& th, & td': { borderColor: 'var(--line)' } }}>
                                                    <TableHead>
                                                        <TableRow sx={{ background: 'var(--bg-10)' }}>
                                                            <TableCell sx={{ fontWeight: 700 }}>Maistinė vertė</TableCell>
                                                            <TableCell align="right" sx={{ fontWeight: 700 }}>Kiekis</TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {rows.map(row => {
                                                            const val = normalized[row.key];
                                                            if (val == null || val === '') return null;
                                                            return (
                                                                <TableRow key={row.key}>
                                                                    <TableCell component="th" scope="row">{row.label}</TableCell>
                                                                    <TableCell align="right">{String(val)}</TableCell>
                                                                </TableRow>
                                                            );
                                                        })}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        );
                                    })()}
                                </Grid>

                                <Grid item xs={12} md={4}>
                                    <Typography variant="h6" sx={{ mb: 1, fontWeight: 700 }}>
                                        Dabartinės kainos
                                    </Typography>
                                    {(() => {
                                        const thirtyDaysAgo = new Date();
                                        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
                                        const byProvider = new Map();
                                        (priceHistory || []).forEach(p => {
                                            const date = new Date(p.date);
                                            if (isNaN(date.getTime()) || date < thirtyDaysAgo) return;
                                            const name = (p.store && p.store.name) ? p.store.name : (p.provider && p.provider.name) ? p.provider.name : 'Unknown';
                                            const prev = byProvider.get(name);
                                            if (!prev || new Date(prev.date) < date) {
                                                byProvider.set(name, { name, price: p.value, date: p.date });
                                            }
                                        });
                                        const items = Array.from(byProvider.values()).sort((a, b) => Number(a.price) - Number(b.price));
                                        const logoFor = (name) => {
                                            if (!name) return null;
                                            const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim().replace(/\s+/g, '-');
                                            const known = {
                                                'barbora': '/img/stores/barbora.png',
                                                'rimi': '/img/stores/rimi.png',
                                                'maxima': '/img/stores/maxima.png',
                                                'lidl': '/img/stores/lidl.png',
                                                'iki': '/img/stores/iki.png',
                                                'norfa': '/img/stores/norfa.png'
                                            };
                                            return known[slug] || null;
                                        };
                                        if (!items.length) return (
                                            <Typography variant="body2" color="text.secondary">Kainų duomenų nėra.</Typography>
                                        );
                                        return (
                                            <List dense sx={{ border: '1px solid var(--line)', borderRadius: 2, background: '#fff' }}>
                                                {items.map((it, idx) => {
                                                    const logo = logoFor(it.name);
                                                    return (
                                                        <ListItem key={idx} sx={{ py: 0.5 }}>
                                                            <ListItemAvatar>
                                                                {logo ? (
                                                                    <Avatar src={logo} alt={it.name} variant="rounded" sx={{ width: 32, height: 32 }} />
                                                                ) : (
                                                                    <Avatar variant="rounded" sx={{ width: 32, height: 32, bgcolor: 'var(--bg-10)', color: 'var(--text-secondary)' }}>
                                                                        <Store fontSize="small" />
                                                                    </Avatar>
                                                                )}
                                                            </ListItemAvatar>
                                                            <ListItemText
                                                                primary={it.name}
                                                                secondary={`€${Number(it.price).toFixed(2)}`}
                                                                primaryTypographyProps={{ fontSize: 14, fontWeight: 600 }}
                                                                secondaryTypographyProps={{ fontSize: 13 }}
                                                            />
                                                        </ListItem>
                                                    );
                                                })}
                                            </List>
                                        );
                                    })()}
                                </Grid>
                            </Grid>
                        </Box>
                    </Grid>
                </Grid>
            </Box>

            {/* Combined Price Chart + Recipe in one row */}
            <Paper elevation={2} sx={{ p: { xs: 2, sm: 3 }, mb: 4, borderRadius: 2 }}>
                <Grid container spacing={2} alignItems="stretch">
                    {/* Left: Price History Chart (1/3) */}
                    <Grid item xs={12} md={4}>
                        <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <TrendingUp />
                            Kainų istorija
                        </Typography>
                        {chartData ? (
                            <Box sx={{ height: 300, position: 'relative' }}>
                                <Line data={chartData} options={chartOptions} />
                            </Box>
                        ) : (
                            <Typography variant="body2" color="text.secondary">Kainų duomenų nėra.</Typography>
                        )}
                    </Grid>

                    {/* Right: Recipe (2/3) */}
                    <Grid item xs={12} md={8}>
                        <Typography variant="h6" sx={{ mb: 2 }}>Receptas</Typography>

                        {recipeLoading && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CircularProgress size={20} />
                                <Typography variant="body2">Generuojamas receptas...</Typography>
                            </Box>
                        )}

                        {!recipeLoading && !recipe && (
                            <Typography variant="body2" color="text.secondary">Šiam produktui receptas negeneruojamas.</Typography>
                        )}

                        {!recipeLoading && recipe && (() => {
                            const parsePrice = (val) => {
                                if (val == null) return null;
                                const num = typeof val === 'number' ? val : parseFloat(String(val).replace(',', '.'));
                                return isNaN(num) ? null : num;
                                };
                            const totalPrice = Array.isArray(recipe.ingredients)
                                ? recipe.ingredients.reduce((sum, ing) => {
                                    const p = parsePrice(ing.lastPrice);
                                    return p != null ? sum + p : sum;
                                  }, 0)
                                : 0;
                            const hasAnyPrice = Array.isArray(recipe.ingredients) && recipe.ingredients.some(ing => parsePrice(ing.lastPrice) != null);
                            return (
                                <Box>
                                    <Grid container spacing={2}>
                                        {/* 1st column: Title + illustration */}
                                        <Grid item xs={12} md={4}>
                                            <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{recipe.title}</Typography>
                                                {recipe.caloriesKcal != null && (
                                                    <Chip size="small" label={`~ ${Math.round(recipe.caloriesKcal)} kcal`} />
                                                )}
                                            </Box>
                                            <Box sx={{ position: 'relative', width: '100%', aspectRatio: '1 / 1', border: '1px solid var(--line)', borderRadius: 1, overflow: 'hidden', background: '#fafafa', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                {!recipe.imageUrl ? (
                                                    <Box sx={{ textAlign: 'center', p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                                                        <CircularProgress size={32} />
                                                    </Box>
                                                ) : (
                                                    <img src={recipe.imageUrl} alt={recipe.title} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                                                )}
                                            </Box>
                                            {/* Price chips below image */}
                                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                                                {(() => {
                                                    const formatPrice = (num) => (typeof num === 'number' ? `€${num.toFixed(2)}` : null);
                                                    const purchaseLabel = formatPrice(recipe.totalPurchaseCost) ?? (hasAnyPrice ? `~ €${totalPrice.toFixed(2)}` : '-');
                                                    const usedLabel = formatPrice(recipe.totalUsedCost) ?? (hasAnyPrice ? `~ €${totalPrice.toFixed(2)}` : '-');
                                                    return (
                                                        <>
                                                            <Chip color="primary" size="small" label={`Pirkti: ${purchaseLabel}`} />
                                                            <Chip color="secondary" size="small" label={`Sunaudota: ${usedLabel}`} />
                                                        </>
                                                    );
                                                })()}
                                            </Box>
                                        </Grid>

                                        {/* 2nd column: Instructions */}
                                        <Grid item xs={12} md={4}>
                                            <Typography variant="subtitle2" sx={{ mb: 1 }}>Gaminimas</Typography>
                                            <Box sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.35, fontSize: '0.95rem' }}>
                                                {recipe.instructions}
                                            </Box>
                                        </Grid>

                                        {/* 3rd column: Ingredients (compact) */}
                                        <Grid item xs={12} md={4}>
                                            <Typography variant="subtitle2" sx={{ mb: 1 }}>Ingredientai</Typography>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                {recipe.ingredients?.map((ing, idx) => (
                                                    <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, borderBottom: '1px dashed var(--line)', py: 0.25 }}>
                                                        <Box>
                                                            <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.1 }}>{ing.name}</Typography>
                                                            <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1 }}>{[ing.quantity, ing.unit].filter(Boolean).join(' ')}</Typography>
                                                        </Box>
                                                        <Box>
                                                            {ing.productAlias ? (
                                                                <Link href={`/product/${encodeURIComponent(ing.productAlias)}`}>{ing.lastPrice ? `€${ing.lastPrice}` : 'Peržiūrėti'}</Link>
                                                            ) : (
                                                                <Typography variant="caption" color="text.secondary">-</Typography>
                                                            )}
                                                        </Box>
                                                    </Box>
                                                ))}
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </Box>
                            );
                        })()}
                    </Grid>
                </Grid>
            </Paper>

            {/* Similar Products */}
            {similarProducts.length > 0 && (
                <Paper elevation={2} sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2 }}>
                    <Typography variant="h6" sx={{ mb: 3 }}>
                        Similar Products
                    </Typography>
                    <Grid container spacing={2}>
                        {similarProducts.map((similarProduct, index) => (
                            <Grid item xs={6} sm={4} md={3} lg={2} key={index}>
                                <ProductCard card={similarProduct} index={index} />
                            </Grid>
                        ))}
                    </Grid>
                </Paper>
            )}
        </Container>
    );
}

export default Product;